import { useSelector } from 'react-redux';
import { ActionType, FeatureFlag, RouteName, TextKey } from '@mews-commander/enums';
import { ApplicationState, WithGuideLink, WithServiceId } from '@mews-commander/types';
import { ModalWrapper, ModalBodyWrapper } from '@mews-commander/ui';
import { IconName } from '@optimus-web/core';
import { compactArray } from '@mews/fp';
import { LocalizedText, Substitutes, useFeatureFlag } from '@mews/framework';
import { ResolveRoute, useResolveRoute } from '@mews/routing';
import { TimeUnit } from '@mews/enums';
import { getEnterpriseName } from '@mews-commander/legacy-entities/src/ducks/enterprises';
import {
    getLocalizedServiceName,
    getServiceEnterpriseId,
    getTimeUnitByServiceId,
} from '@mews-commander/legacy-entities/src/ducks/services';
import { getEnterpriseDetailUrl } from '@mews-commander/legacy-entities/src/selectors/enterprises';
import { getServiceIndexUrl } from '@mews-commander/legacy-entities/src/selectors/services';
import { canCurrentProfileManageRestrictions } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { useCommanderSelector } from '@mews-commander/utils';
import { useTabsConfig } from '../modalManager/tabs';
import { HeadingAction } from '../../legacy/React/Components/HeadingAction/Action/types';
import ModalHeader from '../../legacy/React/Components/Common/ModalHeader';
import { Breadcrumbs } from '../../components/BreadcrumbList';
import { useCloseButton } from '../../core/windowManager/hooks';
import { Restrictions } from './components';
import { getRestrictionCreateUrl } from './selectors/routes';

const getTabs = ({
    resolveRoute,
    serviceId,
}: {
    resolveRoute: ResolveRoute;
    serviceId: string;
}): Array<{ key: Mews.TextKey; substitutes?: Substitutes; url: string }> => [
    {
        key: TextKey.ListView,
        url: resolveRoute(RouteName.RestrictionIndex, { query: { OwnerId: serviceId } }),
    },
    {
        key: TextKey.CalendarView,
        url: resolveRoute(RouteName.RestrictionCalendar, { query: { OwnerId: serviceId } }),
    },
];

export const RestrictionCalendarContainer: React.FC<WithServiceId & WithGuideLink> = ({ guideLink, serviceId }) => {
    const resolveRoute = useResolveRoute();
    const serviceDetailUrl = resolveRoute(RouteName.VisitDetail, { serviceId });

    const enterpriseId = useSelector((state: ApplicationState) => getServiceEnterpriseId(state, { id: serviceId }));
    const enterpriseDetailUrl = useSelector((state: ApplicationState) =>
        getEnterpriseDetailUrl(state, { enterpriseId, resolveRoute })
    );
    const enterpriseName = useSelector((state: ApplicationState) => getEnterpriseName(state, { enterpriseId }));
    const multiselectRestrictionEnabled = useFeatureFlag(FeatureFlag.restrictionSplicingEnabled);
    const serviceIndexUrl = useSelector((state: ApplicationState) =>
        getServiceIndexUrl(state, { enterpriseId, resolveRoute })
    );
    const serviceName = useSelector((state: ApplicationState) => getLocalizedServiceName(state, { id: serviceId }));
    const canManageRestrictions: boolean = useSelector(canCurrentProfileManageRestrictions);
    const createRestrictionUrl = useSelector((state: ApplicationState) =>
        getRestrictionCreateUrl(state, { serviceId, resolveRoute })
    );
    const timeUnit = useCommanderSelector((state) => getTimeUnitByServiceId(state, { serviceId }));

    const clearRestrictionsUrl = resolveRoute(RouteName.RestrictionClear, { query: { OwnerId: serviceId } });

    const breadcrumbs: Breadcrumbs = [
        { text: enterpriseName, url: enterpriseDetailUrl },
        { text: <LocalizedText textKey={TextKey.ServicePlural} />, url: serviceIndexUrl },
        { text: serviceName, url: serviceDetailUrl },
        { text: <LocalizedText textKey={TextKey.RestrictionPlural} />, active: true },
    ];

    const closeButton = useCloseButton();

    const actions: HeadingAction[] = compactArray([
        canManageRestrictions
            ? {
                  url: createRestrictionUrl,
                  icon: IconName.Add,
                  text: <LocalizedText textKey={TextKey.Restriction} />,
                  type: ActionType.ModalOpener,
              }
            : null,
        canManageRestrictions && multiselectRestrictionEnabled && timeUnit === TimeUnit.Day
            ? {
                  url: clearRestrictionsUrl,
                  icon: IconName.Minus,
                  text: <LocalizedText textKey={TextKey.DeleteRestrictions} />,
                  type: ActionType.ModalOpener,
              }
            : null,
        {
            url: guideLink,
            icon: IconName.Help,
            text: <LocalizedText textKey={TextKey.Help} />,
            type: ActionType.Link,
            target: '_blank',
        },
    ]);

    const tabsConfig = useTabsConfig({
        tabs: getTabs({ resolveRoute, serviceId }),
        activeTab: TextKey.CalendarView,
    });

    return (
        <ModalWrapper>
            <ModalHeader
                breadcrumbs={breadcrumbs}
                actions={actions}
                tabsConfig={tabsConfig}
                primaryAction={closeButton}
            />
            <ModalBodyWrapper padded>
                <Restrictions serviceId={serviceId} />
            </ModalBodyWrapper>
        </ModalWrapper>
    );
};
